import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CardPreview from "../admin/display/CardPreview";
import { fallbackColour } from "../../utils/viewConfig";
import { attributeFacts } from "./workspaceFormat";

// The view being edited, in the shape the app's own screens read
// (GET /processes/view-config): one entry per object type the view shows,
// and its details as that type's card / hover / quick-search fields. Nothing
// here is invented - the label, the colour and the fields all come from the
// view and the database's own catalogue.
export const previewConfig = (viewName, objects = [], types = []) => {
  const entryOf = (type) => (types || []).find((t) => t.type === type) || null;
  const fields = {};
  (objects || []).forEach((object) => {
    fields[object.type] = (object.attributes || []).filter((attribute) => attribute !== "name");
  });
  return {
    // Marks this as a group's view, so the fields read as a whitelist: a type
    // with no details listed shows its name alone, exactly as users see it.
    view: { name: viewName || "" },
    types: (objects || []).map((object, position) => {
      const entry = entryOf(object.type);
      const isDiagram = Boolean(entry?.isDiagram);
      return {
        objectType: object.type,
        label: object.label || entry?.label || object.type,
        colour: entry?.colour || fallbackColour(object.type),
        icon: entry?.icon || (isDiagram ? "diagram" : "object"),
        isDiagram,
        inExplorer: true,
        inSearch: true,
        inQuickSearch: true,
        inStructure: true,
        position,
      };
    }),
    relationships: [],
    fields: { card: fields, hover: fields, quickSearch: fields },
    settings: {},
  };
};

// A real object of this type to draw, where the catalogue carries one; else
// one built from the example values the catalogue gives per detail, so what
// the panel shows is this database's own wording, not invented text.
export const previewItem = (object, entry) => {
  const attributes = (object?.attributes || []).filter((a) => a !== "name");
  const sample = entry?.sample || (entry?.samples || [])[0] || null;
  if (sample && typeof sample === "object") {
    const picked = { GUID: sample.GUID || "preview", name: sample.name || `Example ${object?.label || object?.type || "object"}` };
    attributes.forEach((key) => {
      if (sample[key] !== undefined && sample[key] !== null && sample[key] !== "") picked[key] = sample[key];
    });
    return picked;
  }
  const facts = attributeFacts(entry);
  const examples = new Map(facts.filter((fact) => fact.example).map((fact) => [fact.name, fact.example]));
  if (examples.size === 0) return null;
  const picked = { GUID: "preview", name: examples.get("name") || `Example ${object?.label || entry?.label || object?.type || "object"}` };
  attributes.forEach((key) => {
    if (examples.has(key)) picked[key] = examples.get(key);
  });
  return picked;
};

// Beside the type list: what a reader with this view meets for the chosen
// object type - a search result, an explorer node and the details panel.
export default function ViewPreviewPanel({ viewName, objects = [], types = [], previewType }) {
  const shown = objects.some((o) => o.type === previewType) ? previewType : objects[0]?.type || "";
  const object = objects.find((o) => o.type === shown) || null;
  const entry = (types || []).find((t) => t.type === shown) || null;
  const config = previewConfig(viewName, objects, types);
  const item = previewItem(object, entry);

  return (
    <Box>
      <CardPreview
        config={config}
        objectType={shown}
        item={item}
        showDetails
        showVisibility={false}
        intro="What someone holding this view meets."
        empty="Switch an object type on to see what people will meet."
      />
      {!item && objects.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Example wording, with the details this view lists.
        </Typography>
      )}
    </Box>
  );
}
